import React, { useEffect, useState } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { ShieldCheck, ShieldAlert, Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

export default function SecurityPage() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [setupData, setSetupData] = useState(null); // {provisioning_uri, qr, manual_secret}
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState(null); // array shown once
  const [disableCode, setDisableCode] = useState('');
  const [copied, setCopied] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await superAdminApi.twofaStatus();
      setStatus(data);
    } catch (e) {
      toast.error('Status konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try {
      const { data } = await superAdminApi.twofaSetup();
      setSetupData(data);
      setRecovery(null);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Einrichtung fehlgeschlagen');
    } finally {
      setBusy(false);
    }
  };

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await superAdminApi.twofaActivate(code.trim());
      setRecovery(data.recovery_codes || []);
      setSetupData(null);
      setCode('');
      toast.success('2FA ist jetzt aktiv');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code ungültig');
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setBusy(true);
    try {
      await superAdminApi.twofaDisable(disableCode.trim());
      setDisableCode('');
      toast.success('2FA wurde deaktiviert');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Code ungültig');
    } finally {
      setBusy(false);
    }
  };

  const copySecret = () => {
    if (setupData?.manual_secret) {
      navigator.clipboard.writeText(setupData.manual_secret);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  };

  if (loading) {
    return <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>;
  }

  const enabled = status?.enabled;

  return (
    <div data-testid="sa-security" className="space-y-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Sicherheit</h1>
        <p className="mt-1 text-sm text-slate-600">Zwei-Faktor-Authentisierung (2FA) für dein Super-Admin-Konto.</p>
      </div>

      {/* Status */}
      <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sa-2fa-status">
        {enabled ? (
          <ShieldCheck className="h-8 w-8 text-emerald-600" />
        ) : (
          <ShieldAlert className="h-8 w-8 text-amber-600" />
        )}
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {enabled ? '2FA ist aktiv' : '2FA ist nicht aktiv'}
          </div>
          <div className="text-xs text-slate-600">
            {enabled
              ? `${status?.recovery_codes_remaining ?? 0} Wiederherstellungs-Codes verbleibend`
              : 'Dein Konto ist nur mit Passwort geschützt. Aktiviere 2FA für mehr Sicherheit.'}
          </div>
        </div>
      </div>

      {/* Recovery codes (shown once after activate) */}
      {recovery && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-500/5 p-5" data-testid="sa-2fa-recovery">
          <div className="text-sm font-semibold text-emerald-700">Wiederherstellungs-Codes</div>
          <p className="mt-1 text-xs text-slate-600">Bewahre diese sicher auf. Jeder Code funktioniert einmal, falls du keinen Zugriff auf deine App hast. Sie werden nur jetzt angezeigt.</p>
          <div className="mt-3 grid grid-cols-2 gap-2 font-mono text-sm text-slate-800">
            {recovery.map((c) => (<div key={c} className="rounded bg-white px-3 py-1.5">{c}</div>))}
          </div>
        </div>
      )}

      {/* Setup flow */}
      {!enabled && !setupData && (
        <button
          onClick={startSetup}
          disabled={busy}
          data-testid="sa-2fa-start"
          className="inline-flex items-center gap-2 rounded-xl bg-indigo-500 px-4 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-60"
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4" />}
          2FA einrichten
        </button>
      )}

      {!enabled && setupData && (
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sa-2fa-setup">
          <div className="text-sm text-slate-700">1. Scanne den QR-Code mit einer Authenticator-App (z. B. Google Authenticator, 1Password).</div>
          {setupData.qr && (
            <img src={setupData.qr} alt="2FA QR-Code" className="h-44 w-44 rounded-lg bg-white p-2" data-testid="sa-2fa-qr" />
          )}
          <div className="flex items-center gap-2 text-xs text-slate-600">
            <span>Oder manuell:</span>
            <code className="rounded bg-slate-50 px-2 py-1 font-mono text-slate-800">{setupData.manual_secret}</code>
            <button onClick={copySecret} className="text-slate-600 hover:text-slate-900" data-testid="sa-2fa-copy-secret">
              {copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
            </button>
          </div>
          <div className="text-sm text-slate-700">2. Gib den aktuellen 6-stelligen Code ein, um zu bestätigen.</div>
          <div className="flex items-center gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              inputMode="numeric"
              data-testid="sa-2fa-activate-input"
              className="w-40 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center font-mono tracking-[0.25em] text-slate-900 focus:border-indigo-500 focus:outline-none"
            />
            <button
              onClick={activate}
              disabled={busy || !code}
              data-testid="sa-2fa-activate"
              className="inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-400 disabled:opacity-60"
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Aktivieren
            </button>
          </div>
        </div>
      )}

      {/* Disable */}
      {enabled && (
        <div className="space-y-3 rounded-2xl border border-slate-200 bg-white p-5" data-testid="sa-2fa-disable-box">
          <div className="text-sm font-semibold text-slate-900">2FA deaktivieren</div>
          <p className="text-xs text-slate-600">Gib zur Bestätigung einen aktuellen Code (oder Wiederherstellungs-Code) ein.</p>
          <div className="flex items-center gap-2">
            <input
              value={disableCode}
              onChange={(e) => setDisableCode(e.target.value)}
              placeholder="Code"
              data-testid="sa-2fa-disable-input"
              className="w-40 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-center font-mono tracking-[0.2em] text-slate-900 focus:border-rose-500 focus:outline-none"
            />
            <button
              onClick={disable}
              disabled={busy || !disableCode}
              data-testid="sa-2fa-disable"
              className="rounded-xl border border-rose-300 bg-rose-500/10 px-4 py-2 text-sm font-semibold text-rose-700 hover:bg-rose-500/20 disabled:opacity-60"
            >
              Deaktivieren
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
